import { AsyncStorageLike } from './types';

const DB_SERVICE_URI = 'luna://com.webos.service.db';

interface StorageRecord {
  _id: string;
  key: string;
  value: string;
}

let appIdPromise: Promise<string> | null = null;

const getAppId = (): Promise<string> => {
  if (!appIdPromise) {
    appIdPromise = new Promise(resolve => {
      window.webOS.fetchAppId((appId: string) => resolve(appId));
    });
  }
  return appIdPromise;
};

const getKindId = (appId: string) => `${appId}.storage:1`;

let kindReadyPromise: Promise<string> | null = null;

const ensureKind = (): Promise<string> => {
  if (!kindReadyPromise) {
    kindReadyPromise = getAppId().then(
      appId =>
        new Promise<string>(resolve => {
          window.webOS.service.request(DB_SERVICE_URI, {
            method: 'putKind',
            parameters: {
              id: getKindId(appId),
              owner: appId,
              indexes: [{ name: 'key', props: [{ name: 'key' }] }],
              // Ties this kind's data to the app's lifecycle so webOS purges
              // it on uninstall, instead of it surviving like localStorage does.
              private: true,
            },
            onSuccess: () => resolve(getKindId(appId)),
            onFailure: () => {
              kindReadyPromise = null;
              resolve(getKindId(appId));
            },
          });
        }),
    );
  }
  return kindReadyPromise;
};

const findRecord = (kindId: string, key: string): Promise<StorageRecord | null> =>
  new Promise(resolve => {
    window.webOS.service.request(DB_SERVICE_URI, {
      method: 'find',
      parameters: {
        query: {
          from: kindId,
          where: [{ prop: 'key', op: '=', val: key }],
        },
      },
      onSuccess: (response: { results: StorageRecord[] }) => {
        resolve(response.results?.[0] || null);
      },
      onFailure: () => resolve(null),
    });
  });

const db8Storage: AsyncStorageLike = {
  getItem: async (key: string) => {
    const kindId = await ensureKind();
    const record = await findRecord(kindId, key);
    return record ? record.value : null;
  },

  setItem: async (key: string, value: string) => {
    const kindId = await ensureKind();
    const existing = await findRecord(kindId, key);
    return new Promise<void>(resolve => {
      if (existing) {
        window.webOS.service.request(DB_SERVICE_URI, {
          method: 'merge',
          parameters: { objects: [{ _id: existing._id, key, value }] },
          onSuccess: () => resolve(),
          onFailure: () => resolve(),
        });
      } else {
        window.webOS.service.request(DB_SERVICE_URI, {
          method: 'put',
          parameters: { objects: [{ _kind: kindId, key, value }] },
          onSuccess: () => resolve(),
          onFailure: () => resolve(),
        });
      }
    });
  },

  removeItem: async (key: string) => {
    const kindId = await ensureKind();
    return new Promise<void>(resolve => {
      window.webOS.service.request(DB_SERVICE_URI, {
        method: 'del',
        parameters: {
          query: {
            from: kindId,
            where: [{ prop: 'key', op: '=', val: key }],
          },
        },
        onSuccess: () => resolve(),
        onFailure: () => resolve(),
      });
    });
  },
};

export default db8Storage;
